import { usePermissions } from '@/composables/usePermissions';
import { useAuthStore } from '@/stores/authStore';

/**
 * Everything that can hide a link or block an address. The navigation and the
 * router guard both ask isGateOpen, so a hidden link and a blocked address
 * always agree.
 *
 * - `entity`: the user can view this model's records (`can_view`).
 * - `page`: the user holds this page grant, such as "Track Matching".
 * - `developer`: the user has `is_developer`.
 * - `model`: the user holds one ability on one model, such as
 *   `permission_management` for the Permissions page.
 *
 * Developers pass every gate.
 */
export type Gate =
    | { kind: 'entity'; permissionKey: string }
    | { kind: 'page'; pageName: string }
    | { kind: 'developer' }
    | { kind: 'model'; ability: 'view' | 'update' | 'create'; permissionKey: string };

export function isGateOpen(gate: Gate): boolean {
    const authStore = useAuthStore();
    const { can, canAccessPage } = usePermissions();
    switch (gate.kind) {
        case 'entity':
            return can('view', gate.permissionKey);
        case 'page':
            return canAccessPage(gate.pageName);
        case 'developer':
            return Boolean(authStore.userData?.is_developer);
        case 'model':
            return can(gate.ability, gate.permissionKey);
    }
}
